import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReusableSelect from "./ReusableSelect";
import moment from 'moment';
import { Checkbox, Input, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import ReusableTable from './ReusableTable';
import './NodeList.less';
import { fetchDeviceListData } from './nodesHelper';
import NodeTypeList from './NodeTypeList';
import { ReloadOutlined } from '@ant-design/icons';

type DataRow = {
  id: string | number;
  [key: string]: any;
};

interface NodeListProps {
  renderItems?: string[];
  onChange?: (data: any) => void;
  autoRefresh?: number; // in minutes
  loadMoreData?: (page: number) => Promise<DataRow[]>; // API call for paginated data
  filters: any;
  showSearch?: boolean;
  isCheckable?: boolean;
  data?: any;
  displayRecords?: number;
  setFilters?: (data: any) => void;
  displayDeviceType?:  boolean;
  FilterStatus?: boolean;
  entityId: string | undefined;
  dataSource?: any[];
  mode: string;
  renderStatusColored?: boolean;

}

// This will be moved to constant file
const columns = [
  { key: 'ranch', title: 'Ranch' },
  { key: 'block', title: 'Block' },
  { key: 'deviceName', title: 'Name' },
  { key: 'deviceIdentifier', title: 'Identifier' },
  { key: 'deviceType', title: 'Type' },
  // { key: 'firmwareType', title: 'Firmware Type' },
  { key: 'fwStatus', title: 'Firmware Status' },
  { key: 'lastFwVersion', title: 'Previous FW Version' },
  { key: 'currentFwVersion', title: 'Current FW Version' },
  { key: 'lastConfigApplyDate', title: 'Latest Apply Date' },
  { key: 'lastFirmwareApplyDate', title: 'Previous Firmware Apply Date' },
  { key: 'updatedAt', title: 'DB Updated Date' },
  { key: 'configSyncStatus', title: 'Status' },
  { key: 'fwSyncStatus', title: 'Status' },
  // { key: 'entity', title: 'Entity' },
];

const NodeList: React.FC<NodeListProps> = ({
  renderItems,
  onChange,
  autoRefresh,
  loadMoreData,
  filters,
  showSearch,
  isCheckable,
  data,
  displayRecords,
  setFilters,
  displayDeviceType, // To render device type on main page
  FilterStatus,
  entityId,
  dataSource,
  mode,
  renderStatusColored = true,
  
}: any) => {
  const [tableData, setTableData] = useState<DataRow[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<(string | number)[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedNodeType, setSelectedNodeType] = useState<string | null>('field_node');
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const { t } = useTranslation();
  const refreshStatusInterval = useRef(null);
  const entityIdRef = useRef(entityId);
  const filtersRef = useRef(filters);
  const REFRESH_INTERVAL = 1000 * 60 * (autoRefresh || 0);
  const deviceTypeMap = {
  field_node: 'FN',
  energy_node: 'EN',
  control_node: 'CN',
}

  // To load data for default selection
  useEffect(() => {
    if (setFilters && displayDeviceType) {
      setFilters((prevFilters: any) => ({
        ...prevFilters,
        type: selectedNodeType,
      }));
    }
  }, [setFilters, displayDeviceType]);
  useEffect(() => {
    if (dataSource) {
     setTableData(dataSource);
    } else if (data) {
      setTableData(data);
    }
  }, [dataSource, data]);
   // Search filter logic
  const filteredData = () => {
    if (!tableData.length) return [];
  
    let filtered = tableData;
  
    // Filter by Status
    if (filters?.status) {
      filtered = filtered.filter((item) =>
        // Filter based on FilterStatus
        (FilterStatus ? item.fwSyncStatus : item.configSyncStatus) === filters.status);
    }
  
    // Filter by Search Query
    if (searchQuery) {
      const searchText = searchQuery.toLowerCase();
      const customFieldsForFilter = ['deviceId', 'deviceName', 'deviceType','deviceIdentifier',
      'currentFwVersion', 'configSyncStatus', 'fwSyncStatus', 'fwStatus'];
      filtered = filtered.filter((item) =>
        renderItems?.some((key) =>
          customFieldsForFilter.includes(key) &&
          item[key]?.toString()?.toLowerCase()?.includes(searchText)
        )
      );
    }

    // Filter by Firmware Versions (currentFwVersion or lastFwVersion)
    if (filters?.currentFwVersion || filters?.lastFwVersion) {
      filtered = filtered.filter((item) =>
        (!filters.currentFwVersion || item.currentFwVersion === filters.currentFwVersion) &&
        (!filters.lastFwVersion || item.lastFwVersion === filters.lastFwVersion)
      );
    }
  
    return filtered;
  };

  useEffect(() => {
    // Whenever the filters change, reset the selected row keys
    setSelectedRowKeys([]);
  }, [filters]);


  // Handle row selection
  const handleRowSelectionChange = (selectedKeys: (string | number)[]) => {
    setSelectedRowKeys(selectedKeys);
    const filteredRows = filteredData();

     // Map selected row keys to their full details
    const selectedRows: any = filteredRows.filter((row) => selectedKeys.includes(row.deviceId));
    if (onChange) {
      onChange(selectedRows);
    }
  };

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

 function refreshUntilUnmount() {
  refreshStatusInterval.current = setInterval(() => {
    const type = filtersRef.current?.type;
    const id = entityIdRef.current;
    if (!type || !id) return;

    setIsAutoRefreshing(true); // Start spinner
    fetchDeviceListData(type, id)
      .then((response) => {
        if (data?.length) {
          const deviceIds = data?.map((d: any) => d?.deviceId);
          const filteredResponse = response?.filter((d: any) => deviceIds?.includes(d?.deviceId));
          setTableData(filteredResponse);
        } else {
          setTableData(response);
        }
      })
      .finally(() => {
          setIsAutoRefreshing(false);
        });
  }, REFRESH_INTERVAL);
}

  function clearRefreshInterval() {
    if (refreshStatusInterval.current) {
      clearInterval(refreshStatusInterval.current);
      refreshStatusInterval.current = null;
    }
  }

  // Trigger the refresh interval when autoRefresh has value
  useEffect(() => {
    if (!autoRefresh) return;

  refreshUntilUnmount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  // Clear the interval when component unmounts
  useEffect(() => () => {
    clearRefreshInterval();
  }, []);

  const handleSelectAllChange = (checked: boolean) => {
    const filteredRows = filteredData();
    if (checked) {
      // Select all rows
      setSelectedRowKeys(filteredRows.map((row) => row.deviceId));
      handleRowSelectionChange(filteredRows.map((row) => row.deviceId));
    } else {
      // Deselect all rows
      setSelectedRowKeys([]);
      handleRowSelectionChange([]);
    }
  };

  const title = (
    <>
      <Checkbox
        checked={(filteredData().length && selectedRowKeys.length === filteredData().length)}
        indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < filteredData().length}
        onChange={(e) => handleSelectAllChange(e.target.checked)}
      />
    </>
  );

  const filteredColumns = !renderItems?.length
    ? columns // Show all columns if renderItems is an empty array
    : columns.filter((col) => renderItems?.includes(col.key));

  const tableColumns: ColumnsType<DataRow> = [
    ...filteredColumns
      .map((col) => ({
        title: col.title,
        dataIndex: col.key,
        key: col.key,
      }))
      .reduce<ColumnsType<DataRow>>((acc, col) => {
        if (col.key === 'deviceName') {
          acc.push({
            title: col.title,
            dataIndex: col.key,
            key: col.key,
            render: (value) => (value ? value : 'No Name'),
          });
          return acc;
        }
         if(col.key === 'deviceType') {
          acc.push({
             ...col,
             render: (value) => deviceTypeMap[value] || value,
          });
          return acc;
        }

        if (
          col.key === 'updatedAt' ||
          col.key === 'lastConfigApplyDate' ||
          col.key === 'lastFirmwareApplyDate'
        ) {
          acc.push({
            title: col.key === 'updatedAt'
            ? (mode === 'ota' ? 'Last Updated Date' : 'DB Updated Date')
            : col.title,
            dataIndex: col.key,
            key: col.key,
            render: (value) =>
              value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : 'No Date Available',
          });
          return acc;
        }
        if (col.key === 'configSyncStatus') {
          
          acc.push({
          title: col.title,
          dataIndex: col.key,
          key: col.key,
            render: (value, record) => {
              if (!renderStatusColored) {
              return value?.charAt(0).toUpperCase() + value?.slice(1);
              }
              const updatedAt = moment(record.updatedAt);
              const lastApplyDate = moment(record.lastConfigApplyDate);
              const isOutdated = updatedAt.isAfter(lastApplyDate, 'minute');

              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                 <span style={{ color: isOutdated ? 'red' : 'inherit' }}>
                   {value?.charAt(0).toUpperCase() + value?.slice(1)}
                 </span>
                {isOutdated && (
                  <ReloadOutlined style={{ color: 'red' }} />
                )}
                </span>
              );
            },
          });
          return acc;
        }
        if (col.key === 'ranch' || col.key === 'block') {
          acc.push({
            ...col,
            render: (_, record) => {
              return record[col.key] ?? 'N/A'
            }
          });
          return acc
        }

        // Default fallback for null/undefined values
        acc.push({
          ...col,
          render: (value) => value ?? 'None',
        });
        return acc;
      }, []),
    ...(isCheckable
      ? [
          {
            title,
            dataIndex: 'select',
            key: 'select',
            render: (_, record) => (
              <Checkbox
                checked={selectedRowKeys.includes(record.deviceId)}
                onChange={(e) =>
                  handleRowSelectionChange(
                    e.target.checked
                      ? [...selectedRowKeys, record.deviceId]
                      : selectedRowKeys.filter((key) => key !== record.deviceId)
                  )
                }
              />
            ),
          },
        ]
      : []),
  ];


  return (
    <div className="node-list-table">
      <div className='flex-row flex-end'>
        {isAutoRefreshing && (
        <div className='auto-refresh-spinner'>
          <Spin size="small" />
        </div>
        )}
        {showSearch && (
        <div className="search-box-align">
          <Input
            placeholder={t("Type here to search...")}
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: 300 }}
          />
        </div>
        )}
      </div>
      <ReusableTable
        columns={tableColumns}
        data={filteredData()}
        isCheckable={false}
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={handleRowSelectionChange}
        displayRecords={displayRecords}
        currentPage={currentPage}
        onPageChange={(page) => setCurrentPage(page)}
      />
    </div>
  );
};

NodeList.defaultProps = {
  showSearch: true,
  isCheckable: true,
  displayRecords: 5,
};

export default NodeList;
